import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Clock } from '../../icons';
import { getApprovalStats, type ApprovalStatsDto } from '../../api/approval';
import { tApproval, type ApprovalI18nKey } from '../../lib/approval-i18n';
import { PageHeader, StatTile, StatTiles, Table } from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './ApprovalStats.css';

/**
 * **결재 현황** — 회사의 결재가 어디서 서는가 (2026-08-31 · 시안 맞춤 5/9).
 *
 * ## 무엇이 없었나
 *
 * ★★ 지금은 「내 문서가 어디까지 갔나」만 볼 수 있다. **회사 전체가 어디서 서는지** 묻는 자리가
 *    없어서 「지출결의서가 대표 단계에서 3일씩 선다」 같은 것을 **아무도 모른다** — 전결 기준을
 *    고치려 해도 근거가 없다.
 *
 * ## ★ 모르는 것을 숫자로 적지 않는다
 *
 * 완료가 0이면 평균은 **「—」**이지 0일이 아니다. 판정된 문서가 없으면 반려율도 「—」다.
 * 0을 적으면 「빠르다」·「반려가 없다」로 읽힌다.
 *
 * ## ⚠ 「재촉」은 만들지 않았다
 *
 * 시안에는 줄마다 「재촉」 단추가 있다. 그런데 **재촉을 보내는 길이 서버에 없다** — 알림을
 * 새로 만들어야 하고, 「누가 누구를 몇 번 재촉했나」도 남아야 한다(안 남기면 재촉이 사적인
 * 압박이 된다). 누르면 아무 일도 안 하는 단추 대신, **누가 들고 있는지 이름을 적는다.**
 */

const PERIODS = [['week', '이번 주'], ['month', '이번 달'], ['quarter', '분기']] as const;

export function ApprovalStatsPage(): JSX.Element {
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter'>('month');
  const [data, setData] = useState<ApprovalStatsDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'forbidden' | 'error'>('loading');

  const load = useCallback(async () => {
    setState('loading');
    try {
      setData(await getApprovalStats(period));
      setState('ok');
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
      // ★ 「볼 수 없다」와 「못 읽었다」는 다른 말이다 — 얼버무리면 담당자가 서버를 의심한다.
      setState(code === 'FORBIDDEN' ? 'forbidden' : 'error');
    }
  }, [period]);

  useEffect(() => { void load(); }, [load]);

  const num = (v: number | null, unit = '') => (v === null ? '—' : `${v}${unit}`);

  /**
   * 양식 이름 — **사람 말로** (2026-08-31 · 시안 F1 대조에서 잡았다).
   * ★ 이 표만 `LEAVE`·`BUSINESS_TRIP` 같은 **코드를 그대로** 보여 주고 있었다(다른 결재 화면은
   *   전부 옮겨 적는다). 담당자가 읽는 자리에 코드가 서면 그것부터 물어보게 된다.
   * ★ 목록에 없는 코드는 **그대로 둔다** — 회사가 만든 양식은 이름이 저마다이고,
   *   억지로 옮기면 없는 이름을 지어내게 된다.
   */
  function typeLabel(code: string): string {
    const key = `approval.type.${code}` as ApprovalI18nKey;
    const label = tApproval(key);
    return label === key ? code : label;
  }

  /**
   * ★★★ **단계별 소요 막대** (2026-08-31 · 시안 F1).
   *
   *    「결재가 느리다」로는 아무것도 못 바꾸지만 「**3단계에서 평균 3.2일**」이면 바꿀 수 있다 —
   *    전결 기준을 올리든, 위임을 걸든. 막대 길이는 **그 양식 안에서의 비율**이다.
   *
   * ★ 단계에 **이름을 붙이지 않는다** — 그때의 직책이 어디에도 안 남는다(지금 직책을 적으면
   *   «그때»가 아니라 «지금»을 적는 것이다). 그래서 「3단계」라고 말한다.
   * ★ 옛 서버는 이 칸을 안 준다 → **아무것도 안 그린다**(빈 막대를 그리면 「0일」로 읽힌다).
   */
  function stepBar(
    steps: ApprovalStatsDto['types'][number]['steps'],
    scale: number,
  ): JSX.Element {
    if (!steps || steps.length === 0) return <span className="asts__dim">—</span>;
    const total = steps.reduce((n, s2) => n + s2.avgDays, 0);
    if (total <= 0) return <span className="asts__dim">같은 날 끝남</span>;
    const worst = steps.reduce((a, b) => (b.avgDays > a.avgDays ? b : a));
    /*
     * ★★★ **한 단계가 붙잡고 있는가**를 색으로 말한다 — 단계가 하나뿐이면 늘 100%라
     *    「그 단계가 문제」가 아니다(그냥 한 단계짜리 결재다). 그래서 **둘 이상일 때만** 칠한다.
     */
    const heavy = steps.length >= 2
      && worst.avgDays / total >= 0.4
      /* ★ **하루도 안 걸리는 단계는 경고가 아니다.** 비율만 보면 0.9일짜리도 빨갛게 되는데,
           그건 「빠른 결재의 마지막 단계」일 뿐이다. 고칠 거리가 되는 것만 눈에 걸리게 한다. */
      && worst.avgDays >= 1;
    return (
      <span className="asts__steps">
        {steps.map((s2) => (
          <i
            key={s2.step}
            className={s2.step === worst.step && heavy ? 'is-worst' : undefined}
            /* ★★ 길이는 **양식끼리 견준 것**이다 — 줄마다 100%로 채우면 0.3일짜리도 길어 보인다. */
            style={{ width: `${Math.max(3, Math.round((s2.avgDays / scale) * 100))}%` }}
            title={`${s2.step}단계 ${s2.avgDays}일 · ${s2.count}건`}
          />
        ))}
        <em className={heavy ? 'is-worst' : undefined}>{worst.step}단계 {worst.avgDays}일</em>
      </span>
    );
  }

  /** 막대의 자 — 가장 오래 서는 양식의 총합. 0이면 1로 둔다(0으로 나누지 않는다). */
  const barScale = Math.max(
    1,
    ...(data?.types ?? []).map((t) => (t.steps ?? []).reduce((n, s2) => n + s2.avgDays, 0)),
  );

  return (
    <div className="asts">
      <PageHeader
        breadcrumb={crumbsFor('/approval/admin/stats')}
        scope={scopeFor('/approval/admin/stats')}
        className="asts__head"
        title="결재 현황"
        description={data
          ? <>{data.from} ~ {data.to} · 문서 {data.summary.drafted}건</>
          : '회사 전체가 어디서 서는가'}
        actions={(
          /*
            ⚠ 기간 고르개는 **아직 손짜기**다 — 「눌러서 거르는 칩」이 정본에 없다(D14 질의 중).
               정본에 서면 이 자리가 첫 이주 대상이다.
          */
          <div className="asts__seg" role="group" aria-label="기간">
            {PERIODS.map(([p, label]) => (
              <button
                key={p} type="button" aria-pressed={period === p}
                className={period === p ? 'is-on' : undefined}
                onClick={() => setPeriod(p)}
              >{label}</button>
            ))}
          </div>
        )}
      />

      {state === 'forbidden' && (
        <p className="asts__error" role="alert">
          <AlertCircle size={14} aria-hidden />결재 현황은 <b>회사 관리자</b>만 볼 수 있습니다.
        </p>
      )}
      {state === 'error' && (
        <p className="asts__error" role="alert">
          <AlertCircle size={14} aria-hidden />불러오지 못했습니다.
        </p>
      )}
      {state === 'loading' && <p className="asts__dim">불러오는 중…</p>}

      {state === 'ok' && data && (
        <>
          {/*
            ★ 정본 `StatTile`(D9-2)로 갈아탔다 (2026-09-06). 「—」와 단위는 컴포넌트가 그린다 —
              ★ **완료가 없으면 «—» 다.** 0일로 적으면 「빠르다」로 읽힌다. 값이 없으면
                단위(「일」)도 안 붙는다 — 「— 일」은 값이 있는 것처럼 읽힌다.
          */}
          <StatTiles ariaLabel="요약">
            <StatTile value={data.summary.drafted} label="상신" />
            <StatTile value={data.summary.approved} label="완료" />
            <StatTile tone="primary" value={data.summary.avgDays} unit="일" label="평균 소요" />
            <StatTile
              tone={data.summary.delayed > 0 ? 'warning' : 'default'}
              value={data.summary.delayed} label={`${data.delayDays}일 이상 지연`}
            />
            <StatTile
              value={data.summary.rejected}
              label={`반려 ${data.summary.rejectRate === null ? '' : `${data.summary.rejectRate}%`}`}
            />
          </StatTiles>

          <div className="asts__two">
            <section className="asts__card" aria-label="양식별">
              <h2 className="asts__cardt">
                양식별 <span className="asts__dim">어느 양식이 오래 서나</span>
              </h2>
              {data.types.length === 0 ? (
                <p className="asts__dim">이 기간에 올린 문서가 없습니다.</p>
              ) : (
                <Table
                  caption="양식별 결재 현황"
                  rows={data.types}
                  rowKey={(t) => t.type}
                  /* ★ 지연이 있는 줄의 경고 줄무늬 — 안 넘기면 말없이 사라진다. */
                  rowClassName={(t) => (t.delayed > 0 ? 'is-warn' : undefined)}
                  columns={[
                    { key: 'type', header: '양식', rowHeader: true, render: (t) => typeLabel(t.type) },
                    { key: 'count', header: '건', numeric: true, render: (t) => t.count },
                    /* ★★★ 시안 F1 의 알맹이 — 「어느 단계에서 오래 서나」. */
                    { key: 'steps', header: '단계별 소요', render: (t) => stepBar(t.steps, barScale) },
                    { key: 'avg', header: '평균', numeric: true, render: (t) => num(t.avgDays, '일') },
                    { key: 'delayed', header: '지연', align: 'center',
                      render: (t) => (t.delayed > 0
                        ? <span className="asts__bd is-warn">{t.delayed}</span>
                        : <span className="asts__dim">0</span>) },
                  ]}
                />
              )}
              <p className="asts__note">
                평균은 <b>끝난 문서만</b> 셉니다 — 안 끝난 것을 섞으면 오래 걸리는 문서가 빠지면서
                <b> 평균이 계속 짧아집니다.</b>
              </p>
            </section>

            <div className="asts__col">
              <section className="asts__card" aria-label="오래 서 있는 문서">
                <h2 className="asts__cardt">
                  <Clock size={14} aria-hidden />오래 서 있는 문서
                </h2>
                {data.stale.length === 0 ? (
                  <p className="asts__dim">하루 넘게 선 문서가 없습니다.</p>
                ) : (
                  <ul className="asts__stale">
                    {data.stale.map((s) => (
                      <li key={s.id}>
                        <b>{s.title}</b>
                        <span className="asts__dim">
                          {s.holderName ?? '담당 미정'} · <b className={s.days >= data.delayDays ? 'asts__late' : undefined}>{s.days}일째</b>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {/*
                  ⚠ 시안에는 줄마다 「재촉」 단추가 있지만 **보내는 길이 서버에 없다.**
                    알림을 새로 만들어야 하고 「누가 누구를 몇 번 재촉했나」도 남아야 한다 —
                    안 남기면 재촉이 사적인 압박이 된다. 누르면 아무 일도 안 하는 단추 대신
                    **누가 들고 있는지 이름을 적는다.**
                */}
                <p className="asts__note">
                  <b>「재촉」은 아직 없습니다</b> — 보내는 길과 함께 <b>「누가 누구를 몇 번 재촉했나」</b>가
                  남아야 만들 수 있습니다. 지금은 <b>누가 들고 있는지</b>를 보고 직접 이야기해 주세요.
                </p>
              </section>

              <section className="asts__card" aria-label="사람별">
                <h2 className="asts__cardt">
                  사람별 <span className="asts__dim">지금 들고 있는 문서</span>
                </h2>
                {data.holders.length === 0 ? (
                  <p className="asts__dim">진행 중인 문서가 없습니다.</p>
                ) : (
                  <ul className="asts__holders">
                    {data.holders.map((h) => (
                      <li key={h.userId}>
                        <b>{h.name}</b>
                        <span className="asts__dim">{h.count}건 · 평균 {num(h.avgDays, '일')}</span>
                        {/* 막대는 건수에 견준다 — 눈으로 「누구 앞에 쌓였나」가 보여야 한다. */}
                        <span className="asts__bar" aria-hidden>
                          <i style={{ width: `${Math.round((h.count / (data.holders[0]?.count || 1)) * 100)}%` }} />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="asts__note">
                  <b>지난 실적이 아니라 «지금 앞에 쌓인 것»</b>입니다 — 끝난 문서는 아무도 들고 있지 않습니다.
                </p>
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ApprovalStatsPage;
